import { html } from 'lit';
import type {
  WebEditorInlineTextStyle,
  WebEditorInlineTextStylePatch,
} from '../../inline-typography.js';
import { clampTypographySize } from '../../typography-scale.js';
import type { InlineTextDraft } from './editorPageConfig.js';
import type { InlineTextGeometry } from './editorPageConfig.js';

interface InlineTextStyleChangeBase {
  elementId: string;
  value: string;
  start: number;
  end: number;
}

export type InlineTextStyleChange = InlineTextStyleChangeBase &
  (
    | { style: Omit<WebEditorInlineTextStyle, 'start' | 'end'> | null; patch?: never }
    | { patch: WebEditorInlineTextStylePatch; style?: never }
  );

interface InlineTypographyControlsOptions {
  draft: InlineTextDraft;
  geometry: InlineTextGeometry;
  styles: WebEditorInlineTextStyle[];
  applyChange: (change: InlineTextStyleChange) => void;
}

const field = <T extends HTMLInputElement | HTMLSelectElement>(form: HTMLFormElement, name: string) =>
  form.elements.namedItem(name) as T | null;

const numericSize = (input: HTMLInputElement | null) => {
  const value = input?.value.trim() ?? '';
  return value && Number.isFinite(Number(value)) ? clampTypographySize(Number(value)) : undefined;
};

const positionContextPopover = (event: MouseEvent) => {
  const trigger = event.currentTarget as HTMLElement;
  const popoverId = trigger.getAttribute('popovertarget');
  if (!popoverId) return;
  window.requestAnimationFrame(() => {
    const popover = document.getElementById(popoverId);
    if (!popover?.matches(':popover-open')) return;
    const triggerRect = trigger.getBoundingClientRect();
    const width = popover.offsetWidth;
    const height = popover.offsetHeight;
    const viewportGap = 10;
    const left = Math.min(
      Math.max(viewportGap, triggerRect.left + triggerRect.width / 2 - width / 2),
      window.innerWidth - width - viewportGap,
    );
    const top =
      triggerRect.top - height - 8 >= viewportGap
        ? triggerRect.top - height - 8
        : Math.min(window.innerHeight - height - viewportGap, triggerRect.bottom + 8);
    popover.style.left = `${Math.round(left)}px`;
    popover.style.top = `${Math.round(Math.max(viewportGap, top))}px`;
  });
};

const positionToolbar = (editor: HTMLElement, toolbar: HTMLFormElement) => {
  const textRect = editor.getBoundingClientRect();
  const selection = window.getSelection();
  const rangeRect =
    selection?.rangeCount && selection.anchorNode && editor.contains(selection.anchorNode)
      ? selection.getRangeAt(0).getBoundingClientRect()
      : null;
  const anchorRect = rangeRect && (rangeRect.width > 0 || rangeRect.height > 0) ? rangeRect : textRect;
  const toolbarWidth = toolbar.offsetWidth;
  const toolbarHeight = toolbar.offsetHeight;
  const gap = 18;
  let left = Math.min(
    Math.max(12, anchorRect.left + anchorRect.width / 2 - toolbarWidth / 2),
    window.innerWidth - toolbarWidth - 12,
  );
  let top = anchorRect.top - toolbarHeight - gap;
  if (top < 68) {
    const right = anchorRect.right + gap;
    const leftSide = anchorRect.left - toolbarWidth - gap;
    if (right + toolbarWidth <= window.innerWidth - 12) left = right;
    else if (leftSide >= 12) left = leftSide;
    else top = anchorRect.bottom + gap;
  }
  toolbar.style.left = `${Math.round(left)}px`;
  toolbar.style.top = `${Math.round(Math.max(68, top))}px`;
};

const rangeIsHighlighted = (
  styles: WebEditorInlineTextStyle[],
  start: number,
  end: number,
) => {
  if (start >= end) return false;
  let coveredUntil = start;
  for (const style of styles
    .filter(entry => entry.highlight && entry.end > start && entry.start < end)
    .sort((left, right) => left.start - right.start)) {
    if (style.start > coveredUntil) return false;
    coveredUntil = Math.max(coveredUntil, style.end);
    if (coveredUntil >= end) return true;
  }
  return false;
};

export const syncInlineTypographyRange = (
  editor: HTMLElement,
  toolbar: HTMLFormElement,
  value: string,
  selectionStart: number,
  selectionEnd: number,
  styles: WebEditorInlineTextStyle[],
) => {
  const start = Math.min(selectionStart, selectionEnd);
  const end = Math.max(selectionStart, selectionEnd);
  const hasSelection = start < end;
  toolbar.dataset.start = String(start);
  toolbar.dataset.end = String(end);
  toolbar.classList.toggle('has-selection', hasSelection);
  if (hasSelection) positionToolbar(editor, toolbar);
  toolbar.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>('input, select, button').forEach(
    control => (control.disabled = !hasSelection),
  );
  const excerpt = toolbar.querySelector<HTMLElement>('[data-vp-inline-selection-excerpt]');
  if (excerpt) excerpt.textContent = hasSelection ? `„${value.slice(start, end)}“` : 'Wort oder Textstelle markieren';
  const current = styles.find(style => style.start <= start && style.end >= end);
  const weight = field<HTMLSelectElement>(toolbar, 'fontWeight');
  const desktop = field<HTMLInputElement>(toolbar, 'fontSizeDesktop');
  const mobile = field<HTMLInputElement>(toolbar, 'fontSizeMobile');
  const color = field<HTMLInputElement>(toolbar, 'color');
  const colorPicker = field<HTMLInputElement>(toolbar, 'colorPicker');
  if (weight) weight.value = current?.fontWeight === undefined ? '' : String(current.fontWeight);
  if (desktop) desktop.value = current?.fontSizeDesktop === undefined ? '' : String(current.fontSizeDesktop);
  if (mobile) mobile.value = current?.fontSizeMobile === undefined ? '' : String(current.fontSizeMobile);
  if (color) color.value = current?.color ?? '';
  if (colorPicker) colorPicker.value = current?.color ?? '#222226';
  const weightTrigger = toolbar.querySelector<HTMLElement>('[data-vp-inline-weight-trigger]');
  const sizeTrigger = toolbar.querySelector<HTMLElement>('[data-vp-inline-size-trigger]');
  const colorTrigger = toolbar.querySelector<HTMLElement>('[data-vp-inline-color-trigger]');
  weightTrigger?.classList.toggle('is-active', current?.fontWeight !== undefined);
  sizeTrigger?.classList.toggle(
    'is-active',
    current?.fontSizeDesktop !== undefined || current?.fontSizeMobile !== undefined,
  );
  colorTrigger?.classList.toggle('is-active', current?.color !== undefined);
  if (colorTrigger) colorTrigger.style.setProperty('--vp-context-color', current?.color ?? '#f4f4f5');
  const highlight = toolbar.querySelector<HTMLButtonElement>('[data-vp-inline-highlight]');
  const highlighted = rangeIsHighlighted(styles, start, end);
  if (highlight) {
    highlight.classList.toggle('is-active', highlighted);
    highlight.setAttribute('aria-pressed', String(highlighted));
    highlight.title = highlighted ? 'Dunkelblaue Hervorhebung entfernen' : 'Dunkelblau hervorheben';
    const label = highlight.querySelector<HTMLElement>('[data-vp-inline-highlight-label]');
    const hint = highlight.querySelector<HTMLElement>('[data-vp-inline-highlight-hint]');
    if (label) label.textContent = highlighted ? 'Hervorhebung entfernen' : 'Dunkelblau hervorheben';
    if (hint) hint.textContent = highlighted ? 'Für die gesamte Auswahl aktiv' : 'Weiße Schrift auf Viva-Blau';
  }
};

const selectionFromForm = (form: HTMLFormElement, draft: InlineTextDraft) => ({
  elementId: draft.elementId,
  value:
    document.querySelector<HTMLElement>('.vp-editor-inline-prosemirror')?.dataset.value ??
    document.querySelector<HTMLTextAreaElement>('.vp-editor-inline-textarea')?.value ??
    draft.value,
  start: Number(form.dataset.start ?? 0),
  end: Number(form.dataset.end ?? 0),
});

export const syncInlineTypographySelection = (
  event: Event,
  toolbarId: string,
  styles: WebEditorInlineTextStyle[],
) => {
  const textarea = event.currentTarget as HTMLTextAreaElement;
  const toolbar = document.getElementById(toolbarId) as HTMLFormElement | null;
  if (toolbar)
    syncInlineTypographyRange(
      textarea,
      toolbar,
      textarea.value,
      textarea.selectionStart,
      textarea.selectionEnd,
      styles,
    );
};

export const renderInlineTypographyControls = (options: InlineTypographyControlsOptions) => {
  const toolbarId = `vp-inline-style-${options.draft.elementId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const weightPopoverId = `${toolbarId}-weight`;
  const sizePopoverId = `${toolbarId}-size`;
  const colorPopoverId = `${toolbarId}-color`;
  const viewportWidth = typeof window === 'undefined' ? 1440 : window.innerWidth;
  const toolbarWidth = 438;
  const gap = 18;
  let left = Math.min(Math.max(12, options.geometry.left), viewportWidth - toolbarWidth - 12);
  let top = options.geometry.top - 58;
  if (top < 68) {
    const right = options.geometry.left + options.geometry.width + gap;
    const leftSide = options.geometry.left - toolbarWidth - gap;
    if (right + toolbarWidth <= viewportWidth - 12) left = right;
    else if (leftSide >= 12) left = leftSide;
    else top = options.geometry.top + options.geometry.minHeight + gap;
  }
  const toolbarStyle = `left: ${Math.round(left)}px; top: ${Math.round(Math.max(68, top))}px;`;
  const preserveTextEditor = (event: Event) => {
    event.stopPropagation();
    const editor = document.querySelector<HTMLElement>('.vp-editor-inline-textarea');
    if (!editor) return;
    editor.dataset.inlineToolbarPointerDown = 'true';
    window.setTimeout(() => delete editor.dataset.inlineToolbarPointerDown, 0);
  };
  return html`
    <form
      id=${toolbarId}
      class="vp-editor-inline-selection-toolbar vp-editor-context-toolbar"
      style=${toolbarStyle}
      role="toolbar"
      aria-label="Formatierung der markierten Textstelle"
      @pointerdown=${preserveTextEditor}
      @click=${preserveTextEditor}
      @submit=${(event: SubmitEvent) => {
        event.preventDefault();
        const form = event.currentTarget as HTMLFormElement;
        const colorValue = field<HTMLInputElement>(form, 'color')?.value.trim();
        options.applyChange({
          ...selectionFromForm(form, options.draft),
          style: {
            fontWeight: Number(field<HTMLSelectElement>(form, 'fontWeight')?.value) || undefined,
            fontSizeDesktop: numericSize(field<HTMLInputElement>(form, 'fontSizeDesktop')),
            fontSizeMobile: numericSize(field<HTMLInputElement>(form, 'fontSizeMobile')),
            color: colorValue && /^#[0-9a-f]{6}$/i.test(colorValue) ? colorValue : undefined,
          },
        });
        form.querySelectorAll<HTMLElement>('[popover]:popover-open').forEach(popover => {
          (popover as HTMLElement & { hidePopover?: () => void }).hidePopover?.();
        });
      }}
    >
      <div class="vp-editor-inline-selection-context" aria-hidden="true">
        <span class="icon-[material-symbols--format-size]" aria-hidden="true"></span>
        <strong data-vp-inline-selection-excerpt>Wort oder Textstelle markieren</strong>
      </div>
      <span class="vp-editor-context-separator" aria-hidden="true"></span>
      <button
        type="button"
        class="vp-editor-context-action"
        data-vp-inline-weight-trigger
        popovertarget=${weightPopoverId}
        title="Schriftstärke"
        aria-label="Schriftstärke der Auswahl"
        disabled
        @click=${positionContextPopover}
      ><span class="vp-editor-context-letter" aria-hidden="true">B</span></button>
      <div id=${weightPopoverId} class="vp-editor-context-popover" popover="auto">
        <label class="vp-editor-context-field">
          <span>Schriftstärke</span>
          <select name="fontWeight" disabled>
            <option value="">Erben</option>
            <option value="100">Thin</option>
            <option value="200">Extra Light</option>
            <option value="300">Light</option>
            <option value="400">Regular</option>
            <option value="500">Medium</option>
            <option value="600">Semibold</option>
            <option value="700">Bold</option>
            <option value="800">Extra Bold</option>
            <option value="900">Black</option>
          </select>
        </label>
      </div>
      <button
        type="button"
        class="vp-editor-context-action"
        data-vp-inline-size-trigger
        popovertarget=${sizePopoverId}
        title="Responsive Schriftgröße"
        aria-label="Schriftgröße für Desktop und Mobile"
        disabled
        @click=${positionContextPopover}
      ><span class="icon-[material-symbols--format-size]" aria-hidden="true"></span></button>
      <div id=${sizePopoverId} class="vp-editor-context-popover" popover="auto">
        <strong class="vp-editor-context-popover-title">Schriftgröße</strong>
        <div class="vp-editor-context-size-grid">
          <label class="vp-editor-context-field">
            <span><span class="icon-[material-symbols--desktop-windows]" aria-hidden="true"></span> Desktop</span>
            <div><input name="fontSizeDesktop" type="number" min="8" max="240" placeholder="Erben" disabled /><small>px</small></div>
          </label>
          <label class="vp-editor-context-field">
            <span><span class="icon-[material-symbols--smartphone]" aria-hidden="true"></span> Mobile</span>
            <div><input name="fontSizeMobile" type="number" min="8" max="240" placeholder="Auto" disabled /><small>px</small></div>
          </label>
        </div>
      </div>
      <button
        type="button"
        class="vp-editor-context-action vp-editor-inline-highlight-toggle"
        data-vp-inline-highlight
        title="Dunkelblau hervorheben"
        aria-label="Ausgewählten Text dunkelblau hervorheben oder Hervorhebung entfernen"
        aria-pressed="false"
        disabled
        @click=${(event: MouseEvent) => {
          const button = event.currentTarget as HTMLButtonElement;
          const form = button.closest('form') as HTMLFormElement;
          options.applyChange({
            ...selectionFromForm(form, options.draft),
            patch: { highlight: button.getAttribute('aria-pressed') === 'true' ? null : true },
          });
        }}
      >
        <span class="vp-editor-inline-highlight-swatch" aria-hidden="true">Aa</span>
      </button>
      <button
        type="button"
        class="vp-editor-context-action vp-editor-context-color-trigger"
        data-vp-inline-color-trigger
        popovertarget=${colorPopoverId}
        title="Schriftfarbe"
        aria-label="Schriftfarbe der Auswahl"
        disabled
        @click=${positionContextPopover}
      ><span class="icon-[material-symbols--format-color-text]" aria-hidden="true"></span></button>
      <div id=${colorPopoverId} class="vp-editor-context-popover" popover="auto">
        <label class="vp-editor-context-field">
          <span>Schriftfarbe</span>
          <div>
            <input
              name="colorPicker"
              type="color"
              .value=${'#222226'}
              title="Schriftfarbe auswählen"
              aria-label="Schriftfarbe auswählen"
              disabled
              @input=${(event: InputEvent) => {
                const form = (event.currentTarget as HTMLElement).closest('form') as HTMLFormElement;
                const color = field<HTMLInputElement>(form, 'color');
                if (color) color.value = (event.currentTarget as HTMLInputElement).value;
              }}
            />
            <input
              name="color"
              type="text"
              placeholder="Erben"
              pattern="#[0-9a-fA-F]{6}"
              aria-label="Schriftfarbe als Hexwert"
              disabled
              @input=${(event: InputEvent) => {
                const input = event.currentTarget as HTMLInputElement;
                const picker = field<HTMLInputElement>(input.closest('form') as HTMLFormElement, 'colorPicker');
                if (picker && /^#[0-9a-f]{6}$/i.test(input.value)) picker.value = input.value;
              }}
            />
          </div>
        </label>
      </div>
      <span class="vp-editor-context-separator" aria-hidden="true"></span>
      <button
        type="button"
        class="vp-editor-context-action is-danger"
        title="Formatierung entfernen"
        aria-label="Formatierung der Auswahl entfernen"
        disabled
        @click=${(event: MouseEvent) => {
          const form = (event.currentTarget as HTMLElement).closest('form') as HTMLFormElement;
          options.applyChange({ ...selectionFromForm(form, options.draft), style: null });
        }}
      ><span class="icon-[material-symbols--format-clear]" aria-hidden="true"></span></button>
      <button
        type="submit"
        class="vp-editor-context-action is-primary"
        title="Formatierung anwenden"
        aria-label="Formatierung anwenden"
        disabled
      ><span class="icon-[material-symbols--check]" aria-hidden="true"></span></button>
    </form>
  `;
};
